import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";

type Props = {
  workspace: Workspace;
  expectedConfirmation: string;
  canHardDelete: boolean;
};

/** Renames + clear/delete actions are co-located here so WorkspaceSettingsTab
 *  doesn't have to own the typed-confirmation flow for two destructive paths. */
export const WorkspaceDangerZoneCard = ({
  workspace,
  expectedConfirmation,
  canHardDelete,
}: Props) => {
  const navigate = useNavigate();
  const { refresh, workspaces, setActive } = useWorkspace();

  const [clearOpen, setClearOpen] = useState(false);
  const [clearText, setClearText] = useState("");
  const [clearing, setClearing] = useState(false);

  const [hardOpen, setHardOpen] = useState(false);
  const [hardText, setHardText] = useState("");
  const [hardDeleting, setHardDeleting] = useState(false);

  const callDeleteWorkspace = async (mode: "clear" | "hard", confirmation: string) => {
    const { data, error } = await supabase.functions.invoke("delete-workspace", {
      body: { workspaceId: workspace.id, mode, confirmation },
    });
    if (error) {
      const message = (data as { error?: string })?.error ?? error.message;
      throw new Error(message);
    }
    return data;
  };

  const runClear = async () => {
    setClearing(true);
    try {
      await callDeleteWorkspace("clear", clearText);
      toast.success("Workspace data cleared");
      setClearOpen(false);
      setClearText("");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setClearing(false);
    }
  };

  const runHardDelete = async () => {
    setHardDeleting(true);
    try {
      await callDeleteWorkspace("hard", hardText);
      toast.success("Workspace deleted");
      const fallback = workspaces.find((w) => w.id !== workspace.id);
      if (fallback) setActive(fallback.id);
      await refresh();
      setHardOpen(false);
      setHardText("");
      navigate("/excerpts");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setHardDeleting(false);
    }
  };

  return (
    <>
      <Card className="shadow-soft border-destructive/30">
        <CardHeader><CardTitle className="text-destructive">Workspace danger zone</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Button variant="destructive" onClick={() => { setClearText(""); setClearOpen(true); }}>
              Clear this workspace's data
            </Button>
            <p className="text-xs text-muted-foreground">
              Removes every excerpt, library item, tag, category, brand color, gradient preset, Readwise highlight,
              Composer record, and usage counter in <span className="font-medium text-foreground">{expectedConfirmation}</span>.
              The workspace itself stays open. This does not close your account, remove billing records, or cancel your subscription.
            </p>
          </div>
          {canHardDelete && (
            <div className="space-y-1.5">
              <Button variant="destructive" onClick={() => { setHardText(""); setHardOpen(true); }}>
                Delete this workspace permanently
              </Button>
              <p className="text-xs text-muted-foreground">
                Permanently deletes <span className="font-medium text-foreground">{expectedConfirmation}</span> and
                everything inside it — no archive, no recovery. Your account and primary workspace are unaffected.
                Only the plan owner can perform this action.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={clearOpen} onOpenChange={(open) => { setClearOpen(open); if (!open) setClearText(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all data in this workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every excerpt, library item, tag, category, brand color, gradient preset,
              Readwise highlight, Composer record, and usage counter in this workspace. The workspace itself
              stays open. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="clear-confirm" className="text-sm">
              Type <span className="font-mono font-semibold text-foreground">{expectedConfirmation}</span> to confirm
            </Label>
            <Input
              id="clear-confirm"
              value={clearText}
              onChange={(e) => setClearText(e.target.value)}
              placeholder={expectedConfirmation}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runClear(); }}
              disabled={clearing || clearText.trim() !== expectedConfirmation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearing ? "Clearing…" : "Clear workspace data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={hardOpen} onOpenChange={(open) => { setHardOpen(open); if (!open) setHardText(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the workspace and every record inside it forever. Your account and primary workspace are
              unaffected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="hard-confirm" className="text-sm">
              Type <span className="font-mono font-semibold text-foreground">{expectedConfirmation}</span> to confirm
            </Label>
            <Input
              id="hard-confirm"
              value={hardText}
              onChange={(e) => setHardText(e.target.value)}
              placeholder={expectedConfirmation}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={hardDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runHardDelete(); }}
              disabled={hardDeleting || hardText.trim() !== expectedConfirmation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {hardDeleting ? "Deleting…" : "Delete workspace forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
